import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Target, Wallet, AlertTriangle, RefreshCw, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export function LeadOpsDashboard() {
  const { hasRole } = useAuth();
  const isLead = hasRole('lead_ops_lead');

  const { data, isLoading } = useQuery({
    queryKey: ['leadops-stats'],
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86400_000).toISOString();
      const { data: charges } = await supabase
        .from('lead_charges' as any)
        .select('amount_etb, status, created_at')
        .gte('created_at', since);
      const list = (charges as any[]) || [];
      const charged = list.filter((c) => c.status === 'charged');
      const disputed = list.filter((c) => c.status === 'disputed');
      const refunded = list.filter((c) => c.status === 'refunded');
      const revenue = charged.reduce((s, c) => s + Number(c.amount_etb || 0), 0);
      return { charged: charged.length, disputed: disputed.length, refunded: refunded.length, revenue };
    },
  });

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Target className="h-6 w-6 text-primary" />
          Lead Operations {isLead ? 'Lead' : 'Agent'}
        </h1>
        <p className="text-sm text-muted-foreground">Run the pay-per-lead marketplace: pricing, charges, disputes, and wallet top-ups.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Wallet className="h-8 w-8 text-emerald-500" />
          <div><p className="text-2xl font-bold">{data?.charged}</p><p className="text-xs text-muted-foreground">Charges (30d)</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <DollarSign className="h-8 w-8 text-blue-500" />
          <div><p className="text-2xl font-bold">{(data?.revenue || 0).toLocaleString()}</p><p className="text-xs text-muted-foreground">Revenue ETB (30d)</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <AlertTriangle className="h-8 w-8 text-amber-500" />
          <div><p className="text-2xl font-bold">{data?.disputed}</p><p className="text-xs text-muted-foreground">Open Disputes</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <RefreshCw className="h-8 w-8 text-red-500" />
          <div><p className="text-2xl font-bold">{data?.refunded}</p><p className="text-xs text-muted-foreground">Refunded (30d)</p></div>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button asChild variant="outline" className="justify-start"><Link to="/dashboard/lead-marketplace"><Target className="h-4 w-4 mr-2" /> Open Marketplace Console</Link></Button>
          <Button asChild variant="outline" className="justify-start"><Link to="/dashboard/revenue"><DollarSign className="h-4 w-4 mr-2" /> Revenue Dashboard</Link></Button>
        </CardContent>
      </Card>
    </div>
  );
}
